import { Link } from 'react-router-dom';
import VuiBox from 'components/VuiBox';
import VuiTypography from 'components/VuiTypography';
import { IoWarning } from 'react-icons/io5';

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

// Helper function to get status color and styling
const getStatusStyling = (status) => {
  const statusLower = status?.toLowerCase() || '';
  
  switch (statusLower) {
    case 'pending':
      return { color: '#FFB547', text: 'Pending' };
    case 'submitted':
      return { color: '#0075FF', text: 'Submitted' };
    case 'removed':
      return { color: '#4CAF50', text: 'Removed' };
    case 'rejected':
      return { color: '#F44336', text: 'Rejected' };
    case 'approved':
      return { color: '#4CAF50', text: 'Approved' };
    default:
      return { color: '#9E9E9E', text: status || 'Unknown' };
  }
};

// Helper function to get platform name from URL
const getPlatformName = (postUrl) => {
  if (!postUrl) return 'N/A';
  try {
    const url = new URL(postUrl);
    return url.hostname.replace('www.', '');
  } catch (error) {
    return 'Invalid URL';
  }
};

// Transform API data to table format
export const transformTakedownsData = (apiData) => {
  // Handle both optimized response structure and legacy array format
  let dataArray;
  let paginationInfo = null;
  
  if (apiData && apiData.data && Array.isArray(apiData.data)) {
    // New optimized response structure
    dataArray = apiData.data;
    paginationInfo = apiData.pagination;
  } else if (Array.isArray(apiData)) {
    // Legacy array format
    dataArray = apiData;
  } else {
    console.error('Invalid API data format:', apiData);
    return { columns: [], rows: [], pagination: null };
  }

  const columns = [
    { 
      Header: 'TAKEDOWN ID', 
      accessor: 'takedownId', 
      width: '12%',
    },
    { 
      Header: 'INFRINGEMENT TYPE', 
      accessor: 'infringementType', 
      width: '10%',
    },
    { 
      Header: 'MEDIA TYPE', 
      accessor: 'mediaType', 
      width: '8%',
    },
    { 
      Header: 'STATUS', 
      accessor: 'status', 
      width: '10%',
    },
    { 
      Header: 'PLATFORM', 
      accessor: 'platform', 
      width: '10%',
    },
    { 
      Header: 'THREAT ACTOR', 
      accessor: 'threatActor', 
      width: '10%',
    },
    { 
      Header: 'LAST ACTIVITY', 
      accessor: 'dateLastActivity', 
      width: '10%',
    },
  ];

  const rows = dataArray.map((item) => {
    const statusStyling = getStatusStyling(item.takedown_status);
    const platformName = getPlatformName(item.post_url);
    
    return {
      takedownId: (
        <Link to={`/dashboards/takedowns/${item.id}/details`} style={{ textDecoration: 'none', color:"white"}}>
          <VuiTypography 
            variant="button" 
            color="white" 
            fontWeight="medium" 
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            {item.id}
          </VuiTypography>
        </Link>
      ),
      infringementType: (
        <VuiTypography variant="button" color="white" fontWeight="regular">
          {item.violation_type || 'N/A'}
        </VuiTypography>
      ),
      mediaType: (
        <VuiTypography variant="button" color="white" fontWeight="regular">
          {item.media_type ? item.media_type.charAt(0).toUpperCase() + item.media_type.slice(1) : 'N/A'}
        </VuiTypography>
      ),
      status: (
        <VuiBox display="flex" alignItems="center">
          <VuiBox
            width="8px"
            height="8px"
            borderRadius="50%"
            sx={{ backgroundColor: statusStyling.color }}
            mr={1}
          />
          <VuiTypography variant="button" color="white" fontWeight="regular">
            {statusStyling.text}
          </VuiTypography>
        </VuiBox>
      ),
      platform: (
     
        <Link to={`/dashboards/platforms/${platformName}/details`} style={{ textDecoration: 'none', color:"white" }}>
          <VuiTypography
            variant="button"
            color="white"
            fontWeight="medium"
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
          {platformName}
        </VuiTypography>
        </Link>
      ),
      threatActor: (() => {
        const username = item.username;
        const isAnonymousOrNull = !username || 
          username.toLowerCase() === 'null' || 
          username.toLowerCase() === 'n/a' || 
          username.toLowerCase() === 'anonymous';
        
        if (isAnonymousOrNull) {
          return (
            <VuiTypography variant="button" color="white" fontWeight="regular">
              n/a
            </VuiTypography>
          );
        }
        
        return (
          <Link to={`/dashboards/threats/${item.threatID || 'unknown'}/details`} style={{ textDecoration: 'none', color:"white"}}>
          <VuiTypography
            variant="button"
            color="white"
            fontWeight="medium"
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {username}
          </VuiTypography>
        </Link>
        );
      })(),
      dateLastActivity: (
        <VuiTypography variant="button" color="white" fontWeight="regular">
          {formatDate(item.last_activity)}
        </VuiTypography>
      ),
    };
  });

  return { columns, rows, pagination: paginationInfo };
};

// Filter data based on active filter
export const filterTakedownsData = (data, activeFilter) => {
  if (!activeFilter || !Array.isArray(data)) {
    return data;
  }

  return data.filter((item) => {
    switch (activeFilter) {
      case 'requiresApproval':
        return item.takedown_status?.toLowerCase() === 'pending';
      case 'Explicit':
        return item.violation_type?.toLowerCase() === 'explicit';
      case 'Copyright':
        return item.violation_type?.toLowerCase() === 'copyright';
      default:
        return true;
    }
  });
};

export default { transformTakedownsData, filterTakedownsData };
